import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { Row, Col, Button } from 'react-bootstrap';
import { useRole } from '../../../hooks/useRole';
import adminService from '../../../services/adminService';
import { ADMIN_COPY } from '../adminCopy';
import { ADMIN_PATHS } from '../adminPaths';
import { AdminPageHeader } from '../ui';
import { RecentActivitiesTable } from '../dashboard';
import Icon from '../../common/Icons';
import '../AdminPortal.css';

const D = ADMIN_COPY.DASHBOARD;
const N = ADMIN_COPY.NAV;

function DashStatCard({ title, value, icon, tone, subtitle, loading, to }) {
  const body = (
    <>
      <div className="adm-stat-card__head">
        <span className="adm-stat-card__title">{title}</span>
        <span className="adm-stat-card__icon" aria-hidden>
          <Icon name={icon} size={18} />
        </span>
      </div>
      <div className="adm-stat-card__value">
        {loading ? (
          <span className="adm-stat-card__skeleton" aria-label="Chargement" />
        ) : (
          (value ?? 0).toLocaleString('fr-FR')
        )}
      </div>
      {subtitle && !loading ? <div className="adm-stat-card__sub">{subtitle}</div> : null}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={`adm-stat-card adm-stat-card--link adm-stat-card--${tone}`}
        aria-busy={loading}
      >
        {body}
      </Link>
    );
  }

  return (
    <article className={`adm-stat-card adm-stat-card--${tone}`} aria-busy={loading}>
      {body}
    </article>
  );
}

export default function AdminDashboardPage() {
  const { toast } = useOutletContext() || { toast: () => {} };
  const { canManageUsers, canManageCompany, canAccessReports } = useRole();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const loadDashboardData = useCallback(async ({ soft = false } = {}) => {
    try {
      if (soft) setRefreshing(true);
      else setLoading(true);

      const [statsResponse, activitiesResponse] = await Promise.all([
        adminService.getDashboardStats(),
        adminService.getRecentActivities({ limit: 10 })
      ]);
      setStats(statsResponse?.data || {});
      setActivities(activitiesResponse?.data || []);
      setLastRefreshAt(new Date());
    } catch (requestError) {
      console.error('Erreur chargement dashboard admin:', requestError);
      toast('danger', requestError.message || 'Impossible de charger le dashboard.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [toast]);

  useEffect(() => {
    loadDashboardData();
    const interval = setInterval(() => loadDashboardData({ soft: true }), 60000);
    return () => clearInterval(interval);
  }, [loadDashboardData]);

  const cards = useMemo(() => {
    const list = [];
    if (canManageUsers()) {
      list.push({
        key: 'users',
        title: D.STAT_USERS,
        value: stats?.totalUsers,
        icon: 'users',
        tone: 'indigo',
        subtitle: D.STAT_USERS_SUB(stats?.activeUsers || 0),
        to: ADMIN_PATHS.USERS
      });
    }
    if (canManageCompany()) {
      list.push(
        {
          key: 'companies',
          title: D.STAT_COMPANIES,
          value: stats?.totalCompanies,
          icon: 'building',
          tone: 'cyan',
          subtitle: D.STAT_COMPANIES_SUB(stats?.activeCompanies || 0),
          to: ADMIN_PATHS.COMPANIES
        },
        {
          key: 'members',
          title: D.STAT_MEMBERS,
          value: stats?.totalMembers,
          icon: 'user',
          tone: 'teal',
          subtitle: D.STAT_MEMBERS_SUB(
            stats?.totalAgents || 0,
            stats?.totalConjoints || 0,
            stats?.totalEnfants || 0
          ),
          to: ADMIN_PATHS.MEMBERS
        },
        {
          key: 'sites',
          title: D.STAT_SITES,
          value: stats?.totalSites,
          icon: 'warehouse',
          tone: 'amber',
          subtitle: D.STAT_SITES_SUB(stats?.activeSites || 0),
          to: ADMIN_PATHS.SITES
        }
      );
    }
    list.push({
      key: 'consultations',
      title: D.STAT_CONSULTATIONS,
      value: stats?.totalConsultations,
      icon: 'stethoscope',
      tone: 'emerald',
      subtitle: D.STAT_CONSULTATIONS_SUB(stats?.consultationsToday || 0),
      to: canAccessReports() ? ADMIN_PATHS.REPORTS_CLINIQUE : undefined
    });
    if (canAccessReports()) {
      list.push({
        key: 'pending',
        title: D.STAT_PENDING,
        value: stats?.pendingRequests,
        icon: 'exclamationTriangle',
        tone: 'rose',
        subtitle: D.STAT_PENDING_SUB,
        to: `${ADMIN_PATHS.PHARMACY_CENTRAL}/demandes`
      });
    }
    return list;
  }, [stats, canManageUsers, canManageCompany, canAccessReports]);

  const reportHub = useMemo(
    () =>
      canAccessReports()
        ? [
            { to: ADMIN_PATHS.REPORTS_RECEPTION, ...N.reportsReception },
            { to: ADMIN_PATHS.REPORTS_INFIRMERIE, ...N.reportsNursing },
            { to: ADMIN_PATHS.REPORTS_CLINIQUE, ...N.reportsClinical },
            { to: ADMIN_PATHS.REPORTS_PHARMACIE, ...N.reportsPharmacy }
          ]
        : [],
    [canAccessReports]
  );

  const subtitle = `${D.SUBTITLE}${
    lastRefreshAt ? ` · Maj ${lastRefreshAt.toLocaleTimeString('fr-FR')}` : ''
  }`;

  const quickActions = [
    canManageUsers() && {
      id: 'users',
      to: ADMIN_PATHS.USERS,
      icon: 'users',
      title: D.QUICK_USERS
    },
    canManageCompany() && {
      id: 'companies',
      to: ADMIN_PATHS.COMPANIES,
      icon: 'building',
      title: D.QUICK_COMPANIES
    },
    canManageCompany() && {
      id: 'members',
      to: ADMIN_PATHS.MEMBERS,
      icon: 'user',
      title: D.QUICK_MEMBERS
    },
    canManageCompany() && {
      id: 'sites',
      to: ADMIN_PATHS.SITES,
      icon: 'warehouse',
      title: D.QUICK_SITES
    },
    canAccessReports() && {
      id: 'reports',
      to: ADMIN_PATHS.REPORTS_CLINIQUE,
      icon: 'chartBar',
      title: D.QUICK_REPORTS
    },
    canAccessReports() && {
      id: 'pharmacy',
      to: ADMIN_PATHS.PHARMACY_CENTRAL,
      icon: 'pills',
      title: D.QUICK_PHARMACY
    }
  ].filter(Boolean);

  const gridClass =
    cards.length >= 6
      ? 'adm-stats-grid adm-stats-grid--6 mb-4'
      : cards.length === 5
        ? 'adm-stats-grid adm-stats-grid--5 mb-4'
        : 'adm-stats-grid mb-4';

  const showInitialLoading = loading && !stats;

  return (
    <div className="adm-dash">
      <AdminPageHeader
        badge={D.BADGE}
        title={D.TITLE}
        description={subtitle}
        actions={
          <Button
            variant="outline-primary"
            className="adm-btn-ghost rounded-3"
            onClick={() => loadDashboardData({ soft: true })}
            disabled={loading || refreshing}
          >
            <Icon name="refresh" size={16} className="me-2" />
            {D.REFRESH}
          </Button>
        }
      />

      <div className={gridClass}>
        {cards.map((card) => (
          <DashStatCard
            key={card.key}
            title={card.title}
            value={card.value}
            icon={card.icon}
            tone={card.tone}
            subtitle={card.subtitle}
            loading={showInitialLoading}
            to={card.to}
          />
        ))}
      </div>

      {reportHub.length > 0 ? (
        <section className="adm-reports-hub mb-4" aria-label={D.REPORTS_HUB_TITLE}>
          <div className="adm-reports-hub__head">
            <div>
              <h2 className="adm-reports-hub__title">{D.REPORTS_HUB_TITLE}</h2>
              <p className="adm-reports-hub__hint">{D.REPORTS_HUB_HINT}</p>
            </div>
          </div>
          <div className="adm-reports-hub__grid">
            {reportHub.map((item) => (
              <Link key={item.to} to={item.to} className="adm-reports-hub__card">
                <span className="adm-reports-hub__icon" aria-hidden>
                  <Icon name={item.icon} size={18} />
                </span>
                <span className="adm-reports-hub__text">
                  <span className="adm-reports-hub__label">{item.label}</span>
                  <span className="adm-reports-hub__desc">{item.hint}</span>
                </span>
                <Icon name="chevronRight" size={14} className="adm-reports-hub__chevron" aria-hidden />
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <Row className="g-3">
        <Col lg={8}>
          <div className="adm-panel">
            <div className="adm-panel__head adm-panel__head--bar">{D.ACTIVITIES_TITLE}</div>
            <div className="adm-panel__body p-0">
              <RecentActivitiesTable activities={activities} loading={showInitialLoading} />
            </div>
          </div>
        </Col>
        <Col lg={4}>
          <div className="adm-panel">
            <div className="adm-panel__head adm-panel__head--bar">{D.QUICK_ACTIONS_TITLE}</div>
            <div className="adm-panel__body">
              <div className="adm-quick-actions">
                {quickActions.map((action) => (
                  <Link key={action.id} className="adm-quick-action" to={action.to}>
                    <Icon name={action.icon} size={18} />
                    <span>{action.title}</span>
                  </Link>
                ))}
                {!quickActions.length ? (
                  <p className="adm-panel-empty mb-0">{D.NO_ACTIONS}</p>
                ) : null}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
}
